// Component code remains the same until the export
import React, { useState, useEffect, useMemo } from "react";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import { AppDispatch, RootState } from "../../store/store";
import { useDispatch, useSelector } from "react-redux";
import * as style from "./ApplicationAccess.module.scss";
import Card from "./Card";
import {
  clearFeatureSelection,
  updateCurrentStep,
  updateFeatureSelection,
  updateGroupselection,
} from "../../store/slices/featureGroupWithFeatureSlice";
import ApplicationAccessFooter from "../ApplicationAccessFooter/ApplicationAccessFooter";

interface Feature {
  disabled: any;
  id: number;
  featureName: string;
  featureDescription: string | null;
  isChecked?: boolean;
}

interface FeatureGroup {
  groupId: number;
  groupName: string;
  groupDescription: string;
  features: Feature[];
}

interface ExpandedState {
  [key: number]: boolean;
}

const FeatureGroups = ({
  data = [],
  handleSearchValue,
  setSearchValue,
  setSearchedFeatures,
  searchValue,
  searchedFeatures,
  setData,
  isAddingUser = false,
}: any) => {
  const scssClass = style as any;
  const [activeSelect, setActiveSelect] = useState<string>("");
  const [expandedRows, setExpandedRows] = useState<ExpandedState>({});
  const [expandedCards, setExpandedCards] = useState<ExpandedState>({});
  const dispatch = useDispatch<AppDispatch>();

  const { data: featureGroups } = useSelector(
    (state: RootState) => state.featureGroupWithFeature,
  );

  const getExpansion = (value: boolean) => {
    value
      ? setExpandedCards(
          data.reduce((acc: any, grp: any) => {
            acc[grp.groupId] = true;
            return acc;
          }, {} as ExpandedState),
        )
      : setExpandedCards({});
  };
  const handleClear = () => {
    setSearchedFeatures([]);
    setSearchValue("");
    handleSearchValue("");
    dispatch(clearFeatureSelection());
    // setSelectedFeatures({});
  };

  const handleExpandClick = () => {
    setActiveSelect("Expand Section");
    getExpansion(true);
  };

  const handleCollapseClick = () => {
    setActiveSelect("Collapse Section");
    getExpansion(false);
  };

  const toggleFeature = (groupId: number, featureId: number): void => {
    // Update the Redux state
    dispatch(updateFeatureSelection({ groupId, featureId }));

    // Update both searchedFeatures and data states
    const updateFeatureState = (groups: FeatureGroup[]) =>
      groups.map((group) =>
        group.groupId === groupId
          ? {
              ...group,
              features: group.features.map((feature) =>
                feature.id === featureId
                  ? { ...feature, isChecked: !feature.isChecked }
                  : feature,
              ),
            }
          : group,
      );

    setData((prevData: any) => updateFeatureState(prevData));
    setSearchedFeatures((prevSearch: any) => updateFeatureState(prevSearch));
  };

  const toggleAllFeatures = (groupId: number, selected: boolean): void => {
    dispatch(updateGroupselection({ groupId, isGroupChecked: selected }));
    const updateGroupState = (groups: FeatureGroup[]) =>
      groups.map((group) =>
        group.groupId === groupId
          ? {
              ...group,
              features: group.features.map((feature) => ({
                ...feature,
                isChecked: feature.disabled ? feature?.isChecked : selected,
              })),
            }
          : group,
      );
    setData((prevData: any) => updateGroupState(prevData));
    setSearchedFeatures((prevSearch: any) => updateGroupState(prevSearch));
  };

  const rows = useMemo(() => {
    let displayData = data; // Default to all data

    if (searchValue.trim() !== "") {
      displayData = searchedFeatures?.length > 0 ? searchedFeatures : [];
    }

    const rows: FeatureGroup[][] = [];
    for (let i = 0; i < displayData?.length; i += 4) {
      rows.push(displayData?.slice(i, i + 4));
    }
    return rows;
  }, [searchedFeatures, data, searchValue]);

  const toggleRow = (rowIndex: number): void => {
    const shouldExpand = !expandedRows[rowIndex];

    setExpandedRows((prev: any) => ({
      ...prev,
      [rowIndex]: shouldExpand,
    }));

    const rowCards = rows[rowIndex];
    const newExpandedCards = { ...expandedCards };

    rowCards.forEach((card: any) => {
      newExpandedCards[card.groupId] = shouldExpand;
    });

    setExpandedCards(newExpandedCards);
  };

  const toggleAllCards = (expand: boolean): void => {
    const newExpandedCards: ExpandedState = {};
    data.forEach((group: any) => {
      newExpandedCards[group.groupId] = expand;
    });
    setExpandedCards(newExpandedCards);

    const newExpandedRows: ExpandedState = {};
    rows.forEach((_: any, index: any) => {
      newExpandedRows[index] = expand;
    });
    setExpandedRows(newExpandedRows);
  };

  useEffect(() => {
    if (data?.length > 0) {
      toggleAllCards(true);
      // setHasInitialized(true);
    }
  }, [data]);

  return (
    <>
      <div className={scssClass.featureGroupContainer}>
        <span>Feature Groups</span>
        <div className={scssClass.clickSection}>
          <span
            role="button"
            onKeyDown={() => {}}
            tabIndex={0}
            className={`expand ${
              activeSelect === "Expand Section" ? "active" : ""
            }`}
            onClick={handleExpandClick}
          >
            Expand Section
          </span>
          <span className={scssClass.separator}>|</span>
          <span
            role="button"
            tabIndex={0}
            className={`collapse ${
              activeSelect === "Collapse Section" ? "active" : ""
            }`}
            onClick={handleCollapseClick}
            onKeyDown={() => {}}
          >
            Collapse Section
          </span>
        </div>
      </div>
      <div className={scssClass.containermain}>
        <div className={scssClass.cardGridContainer}>
          {searchValue?.trim() !== "" && searchedFeatures?.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              No matching features found.
            </div>
          ) : (
            rows.map((row: any, rowIndex: any) => (
              <div key={rowIndex} className={scssClass.rowMap}>
                <div className={scssClass.carddiv}>
                  {row.map((group: any) => (
                    <Card
                      key={group.groupId}
                      group={group}
                      expandedCards={expandedCards}
                      toggleAllFeatures={toggleAllFeatures}
                      toggleFeature={toggleFeature}
                    />
                  ))}
                </div>
                <button
                  onClick={() => toggleRow(rowIndex)}
                  data-testid="expand-button"
                  className={scssClass.buttonRowExpandCollapse}
                >
                  {expandedRows[rowIndex] ? (
                    <KeyboardArrowUpIcon className={scssClass.chevronUpDown} />
                  ) : (
                    <KeyboardArrowDownIcon
                      className={scssClass.chevronUpDown}
                    />
                  )}
                </button>
              </div>
            ))
          )}
        </div>
      </div>
      <ApplicationAccessFooter
        isBack={isAddingUser}
        isSubmit={false}
        isNextDisabled={
          featureGroups?.filter((d: any) =>
            d?.features?.some((f: any) => f?.isChecked),
          )?.length === 0
        }
        onClear={() => handleClear()}
        onBack={() => dispatch(updateCurrentStep(1))}
        onNext={() => {
          dispatch(updateCurrentStep(3));
        }}
      />
    </>
  );
};

export default FeatureGroups;
